import axios from 'axios'
import { ReaderResponse } from '../models/ReaderResponse'
import { LoginResponse } from 'models/LoginResponse'
import { LoginRequest } from 'models/LoginRequest'
import store from 'store'
import { FirmwareResponse } from 'models/FirmwareResponse'
import { ReportTypeResponse } from 'models/ReportTypeResponse'
import { getJsonDate } from 'utils/utils'
import { EventsResponse } from 'models/EventsResponse'
import { EventTypeResponse } from 'models/EventTypeResponse'

const API_URL = 'https://system.unotex.ru/api'
const AUTH_URL = '/auth'

export enum Roles {
  Exonum = 'exonum',
  AccountListAdmin = 'account_list_admin',
  AccountAdmin = 'account_admin',
  CommandAdmin = 'command_admin',
  EventAdmin = 'event_admin',
  FirmwareAdmin = 'firmware_admin',
  ProfileAdmin = 'profile_admin',
  Reader = 'reader',
  ReportAdmin = 'report_admin',
  ReaderListAdmin = 'reader_list_admin',
  ReaderAdmin = 'reader_admin',
  OperatorAdmin = 'operator_admin',
  OperatorListAdmin = 'operator_list_admin',
}

const hasRole = (role: Roles): boolean => {
  const roles = store.getState().user.roles
  return roles.includes(role)
}

axios.defaults.baseURL = API_URL
axios.defaults.headers.common['api-version'] = 2

axios.interceptors.request.use((config) => {
  if (config.url === AUTH_URL) {
    return config
  }

  const token = store.getState().user.token
  if (token) {
    config.headers.Authorization = `Token ${token}`
  }
  return config
})

axios.interceptors.response.use(
  function (response) {
    return response
  },
  function (error) {
    if (error.response) {
      return Promise.reject(error.response.data)
    }
    return Promise.reject(error.message)
  }
)

export const getReaders = async (): Promise<ReaderResponse[]> => {
  if (!hasRole(Roles.ReaderListAdmin)) {
    return []
  }

  const reponse = await axios.get<ReaderResponse[]>('/admin/readers', {
    params: { include_firmwares: true },
  })
  return reponse?.data ?? []
}

export const getReader = async (
  id: string
): Promise<ReaderResponse | undefined> => {
  if (!hasRole(Roles.ReaderListAdmin)) {
    return
  }

  const reponse = await axios.get<ReaderResponse[]>('/admin/readers', {
    params: { include_firmwares: true, id: id },
  })
  return reponse?.data.at(0)
}

export const extractFirmwaresMap = (
  reader: ReaderResponse
): Map<string, FirmwareResponse> => {
  const map = new Map<string, FirmwareResponse>()

  if (!reader.firmwares) {
    return map
  }

  for (const type of Object.keys(reader.firmwares)) {
    const firmware = reader.firmwares ? reader.firmwares[type] : undefined
    if (firmware) {
      map.set(type, firmware)
    }
  }

  return map
}

export const extractErrorsMap = (
  reader: ReaderResponse
): Map<string, number> => {
  const map = new Map<string, number>()

  if (!reader.errors) {
    return map
  }

  for (const method of Object.keys(reader.errors)) {
    const count = reader.errors[method]
    map.set(method, count)
  }

  return map
}

export const getReportTypes = async (): Promise<ReportTypeResponse[]> => {
  if (!hasRole(Roles.ReportAdmin)) {
    return []
  }

  const reponse = await axios.get<ReportTypeResponse[]>('/admin/report/types')
  return reponse?.data
}

export const getEventTypes = async (): Promise<EventTypeResponse[]> => {
  if (!hasRole(Roles.EventAdmin)) {
    return []
  }

  const reponse = await axios.get<EventTypeResponse[]>('/admin/event/types')
  return reponse?.data
}

export const getEvents = async (
  id: string,
  date: Date
): Promise<EventsResponse[]> => {
  if (!hasRole(Roles.EventAdmin)) {
    return []
  }

  const response = await axios.get<EventsResponse[]>('/admin/events', {
    params: { reader_id: id, date: getJsonDate(date) },
  })
  return response?.data
}

export const getReport = async (
  id: string,
  date: Date,
  reportType: string
): Promise<string> => {
  if (!hasRole(Roles.ReportAdmin)) {
    return ''
  }

  const response = await axios.get<string>('/admin/report', {
    params: { reader_id: id, date: getJsonDate(date), type: reportType },
  })
  return response?.data
}

export const postAuth = async (
  login: string,
  password: string
): Promise<LoginResponse> => {
  const body: LoginRequest = {
    login: login,
    password: password,
  }
  const response = await axios.post<LoginResponse>(AUTH_URL, body)
  return response?.data
}
